import { useNotificationStore } from '@/shared/stores/notification.store';
import { useCallback } from 'react';
import type { NotificationIndicatorsRisk } from '@/domains/notification/notification.entity';

/**
 * Hook Fachada (Facade) para interactuar con las notificaciones de la aplicación.
 * Este es el ÚNICO lugar que debe importar y usar `useNotificationStore` directamente.
 * Proporciona una API estable y segura para el resto de la aplicación.
 */
export const useNotificationData = () => {
  // 1. Selecciona cada pieza del estado de forma individual.
  // Esto es crucial para evitar re-renders innecesarios en los componentes que consumen el hook.
  const notifications = useNotificationStore((state) => state.notifications);
  const isLoaded = useNotificationStore((state) => state.isLoaded);
  const loading = useNotificationStore((state) => state.loading);
  const error = useNotificationStore((state) => state.error);
  const toastsShown = useNotificationStore((state) => state.toastsShown);

  // Acciones del store
  const setNotifications = useNotificationStore(
    (state) => state.setNotifications,
  );
  const setLoading = useNotificationStore((state) => state.setLoading);
  const setError = useNotificationStore((state) => state.setError);
  const markAsLoaded = useNotificationStore((state) => state.markAsLoaded);
  const markToastsAsShown = useNotificationStore(
    (state) => state.markToastsAsShown,
  );
  const reset = useNotificationStore((state) => state.reset);
  const resetData = useNotificationStore((state) => state.resetData);

  // 2. Memoiza las funciones de actualización para mantener una referencia estable.
  const handleSetNotifications = useCallback(
    (notificationData: NotificationIndicatorsRisk) => {
      setNotifications(notificationData);
    },
    [setNotifications],
  );

  const handleSetLoading = useCallback(
    (loadingState: boolean) => {
      setLoading(loadingState);
    },
    [setLoading],
  );

  const handleSetError = useCallback(
    (errorMessage: string | null) => {
      setError(errorMessage);
    },
    [setError],
  );

  const handleMarkAsLoaded = useCallback(() => {
    markAsLoaded();
  }, [markAsLoaded]);

  const handleMarkToastsAsShown = useCallback(() => {
    markToastsAsShown();
  }, [markToastsAsShown]);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleResetData = useCallback(() => {
    resetData();
  }, [resetData]);

  // Funciones computadas - usando selectors directos del store
  const totalNotifications = useNotificationStore((state) =>
    state.getTotalNotifications(),
  );
  const hasAnyNotifications = useNotificationStore((state) =>
    state.hasNotifications(),
  );

  // 3. Devuelve una API pública y limpia para que la usen los componentes.
  return {
    // Estado de las notificaciones
    notifications,
    isLoaded,
    loading,
    error,
    toastsShown,

    // Funciones computadas
    totalNotifications,
    hasNotifications: hasAnyNotifications,

    // Acciones para modificar el estado
    setNotifications: handleSetNotifications,
    setLoading: handleSetLoading,
    setError: handleSetError,
    markAsLoaded: handleMarkAsLoaded,
    markToastsAsShown: handleMarkToastsAsShown,
    reset: handleReset,
    resetData: handleResetData,
  };
};
